/**
 * The backend wraps responses inconsistently. These flatten the common envelopes so callers work
 * with plain objects and arrays — same rules as public-ui-lib's `api-response.util.ts`:
 *   { records: [...], pagination: {...} } → records
 *   { data: X }                           → X
 *   anything else                         → as-is
 */

interface PaginatedBody {
  records: unknown[];
  pagination: object;
}

function isPaginated(body: unknown): body is PaginatedBody {
  if (body === null || typeof body !== 'object') {
    return false;
  }
  const o = body as Record<string, unknown>;
  return (
    Array.isArray(o['records']) && o['pagination'] !== null && typeof o['pagination'] === 'object'
  );
}

export function unwrapApiBody<T>(body: unknown): T | null {
  if (isPaginated(body)) {
    return body.records as T;
  }
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: T }).data;
  }
  return (body as T | null) ?? null;
}

/** The body as an array; anything that is not one reads as empty. */
export function unwrapArray<T>(body: unknown): T[] {
  const value = unwrapApiBody<unknown>(body);
  return Array.isArray(value) ? (value as T[]) : [];
}

/** The body as a single item (or the first element of an array). Throws when there is none. */
export function unwrapItem<T>(body: unknown, errorMessage = 'Expected item in response'): T {
  const value = unwrapApiBody<unknown>(body);
  if (Array.isArray(value)) {
    const [first] = value as T[];
    if (first !== undefined) {
      return first;
    }
  } else if (value && typeof value === 'object') {
    return value as T;
  }
  throw new Error(errorMessage);
}
